import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, QrCode, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useLicense } from "@/context/LicenseContext";
//import errorMap from "node_modules/zod/v3/locales/en.cjs";

const TyreForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isReadOnly } = useLicense();
  const photo = location.state?.photo;  
  const eprelCode = location.state?.eprelCode;
  const eprelData = location.state?.eprelData;
  const fromEPREL = location.state?.fromEPREL;
  const eprelUrl = location.state?.eprelUrl;

  // Compute stagione before useState — Radix UI Select non riflette updates da useEffect dopo il primo render
  const _fourSeasonKw = ["4s", "4seasons", "4 seasons", "all season", "4-season", "4 season", "4stagioni", "4 stagioni", "4-stagioni"];
  const _nomeProdotto = !eprelData ? "" : eprelData?.additionalDetails?.commercialName ? eprelData.additionalDetails.commercialName : eprelData.tyreDesignation;
  const _isFourSeason = !eprelData ? "" : eprelData?.severeSnowTyre && _fourSeasonKw.some(k => (_nomeProdotto || "").toLowerCase().includes(k));
  const initialStagione = !eprelData ? "" : _isFourSeason ? "4 Stagioni" : eprelData.severeSnowTyre ? "Invernale" : "Estivo";
  const initialenergyClass = eprelData?.energyClass || "";

  const [loading, setLoading] = useState(false);
  const [duplicateTire, setDuplicateTire] = useState(null);
  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [formData, setFormData] = useState({
    misura: "",
    marca: "",
    altezza_battistrada: "",
    numero_pneumatici: 1,
    magazzino: "",
    stato: "Nuovo",
    stagione: initialStagione,
    nome_prodotto: "",
    classe_energetica: initialenergyClass,
    codice_carico: "",
    codice_velocita: "",
    selling_price: null,
    purchase_price: null,
    eprelCode: eprelCode ?? null,
  });

  // Pre-fill form with EPREL data if available
  useEffect(() => {
    if (eprelData) {
      const fourSeasonKeywords = ["4s", "4seasons", "4 seasons", "all season", "4-season", "4 season", "4stagioni", "4 stagioni", "4-stagioni"];
      const _nomeProdotto = eprelData?.additionalDetails?.commercialName ? eprelData.additionalDetails.commercialName : eprelData.tyreDesignation;
      const commercialNameLower = (_nomeProdotto || "").toLowerCase();
      const isFourSeason = eprelData.severeSnowTyre && fourSeasonKeywords.some(k => commercialNameLower.includes(k));
      const stagione = isFourSeason ? "4 Stagioni" : eprelData.severeSnowTyre ? "Invernale" : "Estivo";

      setFormData(prev => ({
        ...prev,
        misura: `${eprelData.tyreSection ?? ""}${eprelData.aspectRatio ?? ""}${eprelData.rimDiameter ?? ""}` || prev.misura,
        marca: eprelData.supplierOrTrademark || prev.marca, // eprelData.commercialName || prev.marca,
        nome_prodotto: _nomeProdotto || prev.nome_prodotto, //eprelData.tyreDesignation || prev.nome_prodotto,
        classe_energetica: eprelData.energyClass || prev.classe_energetica,
        codice_carico: eprelData.loadCapacityIndex != null ? String(eprelData.loadCapacityIndex) : prev.codice_carico,
        codice_velocita: eprelData.speedCategorySymbol || prev.codice_velocita,
        stato: "Nuovo",
        stagione,
      }));
    }
  }, [eprelData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "numero_pneumatici"
          ? parseInt(value) || 0
          : name === "selling_price" || name === "purchase_price"
          ? value === "" ? null : parseFloat(value)
          : name === "eprelCode"
          ? value === "" ? null : parseInt(value) || null
          : value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const doCreateTire = async () => {
    if (isReadOnly) {
      toast.error("Licenza scaduta. Rinnova per inserire pneumatici.");
      return;
    }

    setLoading(true);
    try {
      const { eprelCode, ...rest } = formData;
      const payload = {
        photo_base64: photo || "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
        ...rest,
        eprel_code: eprelCode,
      };
      const response = await api.post("/tires", payload);
      toast.success("Pneumatico registrato con successo!");
      navigate(`/tyre/${response.data.code}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Errore nella registrazione del pneumatico";
      toast.error(message);
      // toast.error("Errore nella registrazione del pneumatico");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isReadOnly) {
      toast.error("Licenza scaduta. Rinnova per inserire pneumatici.");
      return;
    }

    if (!photo && !fromEPREL) {
      toast.error("Nessuna foto selezionata");
      navigate("/capture");
      return;
    }

    if (!formData.misura || !formData.marca || !formData.magazzino) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (!user?.email) {
      toast.error("Utente non autenticato");
      navigate("/login");
      return;
    }

    if (formData.stato !== "Nuovo") {
      await doCreateTire();
      return;
    }

    setLoading(true);
    try {
      const { eprelCode, ...rest } = formData;
      const checkPayload = {
        ...rest,
        eprel_code: eprelCode,
      };
      const checkRes = await api.post("/tires/check-exists", checkPayload);
      setDuplicateTire(checkRes.data);
    } catch (err) {
      if (err.response?.status === 404) {
        await doCreateTire();
      } else {
        toast.error("Errore nella verifica del pneumatico");
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const goToCapture = () => {
    navigate("/capture");
  };

  return (
    <div data-testid="tyre-form" className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate("/capture")}
          className="h-12 w-12 p-0"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dati Pneumatico</h1>
          <p className="text-muted-foreground text-sm">
            {fromEPREL ? "Dati importati da EPREL - completa i campi mancanti" : "Compila le informazioni del pneumatico"}
          </p>
        </div>
      </div>

      {duplicateTire && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <Card className="w-full max-w-sm bg-card border-yellow-500/40 shadow-2xl">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="font-semibold text-yellow-500 text-base">Pneumatico già esistente</p>
                <button
                  type="button"
                  onClick={() => setDuplicateTire(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors ml-3 mt-0.5"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Esiste già un pneumatico con questi dati (codice:{" "}
                <span className="font-mono font-bold">{duplicateTire.code}</span>).
                Vuoi inserirlo come nuovo record oppure modificare quello esistente?
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-10"
                  onClick={() => { setDuplicateTire(null); doCreateTire(); }}
                  disabled={isReadOnly}
                >
                  Inserisci nuovo
                </Button>
                <Button
                  type="button"
                  className="flex-1 h-10 bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => navigate(`/tyre/${duplicateTire.code}`, { state: { editMode: true } })}
                >
                  Modifica esistente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {fromEPREL && (
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <QrCode className="text-green-500 mt-1" size={24} />
              <div className="flex-1">
                <p className="font-semibold text-green-500">QR EPREL Scansionato</p>
                <p className="text-sm text-muted-foreground">
                  {eprelData?.tyreDesignation || eprelData?.tyreSection 
                    ? "Verifica e completa i dati mancanti"
                    : "Completa i dati del pneumatico manualmente"}
                </p>
                {eprelUrl && (
                  <a 
                    href={eprelUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline mt-1 inline-block"
                  >
                    Visualizza su EPREL →
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Photo Preview */}
        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-0">
            {photo ? (
              <img
                data-testid="form-photo-preview"
                src={photo}
                alt="Foto pneumatico"
                className="w-full aspect-video object-cover"
              />
            ) : (
              <div 
                className="w-full aspect-video bg-secondary flex flex-col items-center justify-center cursor-pointer hover:bg-secondary/80 transition-colors"
                onClick={goToCapture}
              >
                <QrCode size={48} className="text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">Clicca per aggiungere foto</p>
              </div>
            )}
          </CardContent>
          <div className="border-t border-border">
            <button
              type="button"
              onClick={() => setShowExtraInfo(!showExtraInfo)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
            >
              <span className="font-medium">Info aggiuntive</span>
              <ChevronDown 
                size={18} 
                className={`transition-transform duration-200 ${showExtraInfo ? 'rotate-180' : ''}`}
              />
            </button>
            {showExtraInfo && (
              <div className="px-4 pb-4 space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="eprelCode" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Codice EPREL
                  </Label>
                  <Input
                    id="eprelCode"
                    name="eprelCode"
                    data-testid="input-eprel-code"
                    type="number"
                    placeholder="es. 590129"
                    value={formData.eprelCode ?? ""}
                    onChange={handleChange}
                    className="h-10 bg-input/50 border-input font-mono"
                  />
                  {formData.eprelCode && (
                    <a
                      href={`https://eprel.ec.europa.eu/qr/${formData.eprelCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <QrCode size={12} />
                      Visualizza su EPREL →
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Form */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Informazioni Base</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stato" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Stato *
                  </Label>
                  <Select value={formData.stato} onValueChange={(v) => handleSelectChange("stato", v)}>
                    <SelectTrigger data-testid="select-stato" className="h-12 bg-input/50">
                      <SelectValue placeholder="Stato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Nuovo">Nuovo</SelectItem>
                      <SelectItem value="Usato">Usato</SelectItem>
                      <SelectItem value="Deposito">Deposito</SelectItem>
                      <SelectItem value="Registro PFU">Registro PFU</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stagione" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Stagione
                  </Label>
                  <Select value={formData.stagione} onValueChange={(v) => handleSelectChange("stagione", v)}>
                    <SelectTrigger data-testid="select-stagione" className="h-12 bg-input/50">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Estivo">Estivo</SelectItem>
                      <SelectItem value="Invernale">Invernale</SelectItem>
                      <SelectItem value="4 Stagioni">4 Stagioni</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nome_prodotto" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Nome Prodotto
                </Label>
                <Input
                  id="nome_prodotto"
                  name="nome_prodotto"
                  data-testid="input-nome-prodotto"
                  placeholder="es. Pilot Sport 5"
                  value={formData.nome_prodotto}
                  onChange={handleChange}
                  className="h-12 bg-input/50 border-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="marca" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Marca *
                </Label>
                <Input
                  id="marca"
                  name="marca"
                  data-testid="input-marca"
                  placeholder="es. Michelin"
                  value={formData.marca}
                  onChange={handleChange}
                  className="h-12 bg-input/50 border-input"
                  disabled={fromEPREL}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="misura" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Misura *
                </Label>
                <Input
                  id="misura"
                  name="misura"
                  data-testid="input-misura"
                  placeholder="es. 205/55 R16"
                  value={formData.misura}
                  onChange={handleChange}
                  className="h-12 bg-input/50 border-input font-mono"
                  disabled={fromEPREL}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="codice_carico" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Cod. Carico
                  </Label>
                  <Input
                    id="codice_carico"
                    name="codice_carico"
                    data-testid="input-codice-carico"
                    placeholder="es. 91"
                    value={formData.codice_carico}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input font-mono"
                    disabled={fromEPREL}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="codice_velocita" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Cod. Velocità
                  </Label>
                  <Input
                    id="codice_velocita"
                    name="codice_velocita"
                    data-testid="input-codice-velocita"
                    placeholder="es. V"
                    value={formData.codice_velocita}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input font-mono"
                    disabled={fromEPREL}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="classe_energetica" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Classe Energetica
                </Label>
                <Select value={formData.classe_energetica} onValueChange={(v) => handleSelectChange("classe_energetica", v)} disabled={fromEPREL}>
                  <SelectTrigger data-testid="select-classe-energetica" className="h-12 bg-input/50">
                    <SelectValue placeholder="Seleziona" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                    <SelectItem value="D">D</SelectItem>
                    <SelectItem value="E">E</SelectItem>
                    <SelectItem value="F">F</SelectItem>
                    <SelectItem value="G">G</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="altezza_battistrada" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Altezza Battistrada
                </Label>
                <Input
                  id="altezza_battistrada"
                  name="altezza_battistrada"
                  data-testid="input-altezza"
                  placeholder="es. 6mm"
                  value={formData.altezza_battistrada}
                  onChange={handleChange}
                  className="h-12 bg-input/50 border-input font-mono"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numero_pneumatici" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Quantità
                  </Label>
                  <Input
                    id="numero_pneumatici"
                    name="numero_pneumatici"
                    data-testid="input-numero"
                    type="number"
                    min="1"
                    max="99999"
                    value={formData.numero_pneumatici}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="magazzino" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Magazzino *
                  </Label>
                  <Input
                    id="magazzino"
                    name="magazzino"
                    data-testid="input-magazzino"
                    placeholder="es. A1"
                    value={formData.magazzino}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="selling_price" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Prezzo Vendita
                  </Label>
                  <Input
                    id="selling_price"
                    name="selling_price"
                    data-testid="input-selling-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="es. 49.90"
                    value={formData.selling_price ?? ""}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_price" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Prezzo Acquisto
                  </Label>
                  <Input
                    id="purchase_price"
                    name="purchase_price"
                    data-testid="input-purchase-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="es. 35.00"
                    value={formData.purchase_price ?? ""}
                    onChange={handleChange}
                    className="h-12 bg-input/50 border-input font-mono"
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="submit-btn"
                disabled={loading || isReadOnly}
                className="w-full h-12 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 btn-glow mt-6"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Salva Pneumatico
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TyreForm;
